import { v4 as uuidv4 } from 'uuid'
import { type Nominal, type Nullable, type NominalUUID, createStringEnum } from '../../lib/typeHelpers.no-deps'
import type { FileId, FileName } from '../files/types.no-deps'
import type { ReportClientId } from '../reports/types.no-deps'
import type { FileExtension, FileSelectExtension } from '../files/fileExtensions.no-deps'
import { omit } from '../../lib/functional/object.no-deps'
import type * as Guide from '../guides/types.no-deps'
import type { RichTextParagraph } from '../richText/types.no-deps'
import type { Expression } from './Expression.no-deps'
import type { LoopPath } from './LoopPath.no-deps'
import type { UnitFilter } from './UnitFilter.no-deps'
import type { EncryptedText } from '../../lib/crypto-node'

export type AnswerId = NominalUUID<'FlowAnswerId'>
export type StepId = NominalUUID<'FlowStepId'>
export type ElementId = NominalUUID<'FlowElementId'>
export type PhotoFileName = FileName
export type ReportId = ReportClientId

export const createAnswerId = () => uuidv4() as AnswerId
export const createStepId = () => uuidv4() as StepId
export const createElementId = () => uuidv4() as ElementId

/**
 * Originally flows had choice ids that were numbers, but we then transitioned to uuids.
 * Rather than a database migration, we opted to just move them over everytime a flow is saved.
 * So hopefully they've all migrated by now but we aren't sure yet #4917
 */
export type ChoiceId = Nominal<number, 'FlowChoiceId'> | NominalUUID<'FlowChoiceId'>

export const photoFileNameToFileId = (fileName: FileName) => fileName.replace(/\..+/, '') as FileId
export const createFileName = (fileId: FileId, extension: FileExtension) => `${fileId}${extension}` as FileName

//
// Flow

export type Flow = Section & {
    titleAssigningElement?: ElementId
    titleAppendingElements?: ElementId[]
    templates?: StepTemplate[]
}

export interface StepTemplate {
    step: Step
}

export interface Section {
    id: ElementId
    type: 'section'
    steps: Step[]
    subjectType?: SubjectType
}

export interface HasSteps {
    steps: Step[]
}

export type SubjectType = SubjectTypeUnit

export interface SubjectTypeUnit {
    type: 'SubjectTypeUnit'
    unitType: number
}

export function isSubjectTypeUnit(subjectType: SubjectType): subjectType is SubjectTypeUnit {
    return subjectType.type === 'SubjectTypeUnit'
}

export type GuidesDynamicFilter = 'reportSubject' | 'loopSubject' | null

export type Step = ConcreteStep | PlaceholderStep

export type ConcreteStep = {
    id: StepId
    type?: 'concrete' // Optional for backwards compatibility with steps created before there were multiple types
    label: string
    elements: Element[]
    defaultRoute: DefaultStepRoute
    conditionalRoutes: ConditionalRoute[]
    guides?: Guide.Id[]
    guidesDynamicFilter?: GuidesDynamicFilter
}

type PlaceholderStep = {
    id: StepId
    type: 'placeholder'
    prompt: string
}

export function isConcreteStep(step: Step): step is ConcreteStep {
    return !step.type || step.type === 'concrete'
}

export function isPlaceholderStep(step: Step): step is PlaceholderStep {
    return step.type && step.type === 'placeholder'
}

export type DefaultStepRoute = StepId | 'Step.EndOfSection' | 'Step.NextStep'

export type ConditionalRoute = ElementConditionalRoute | UnitDataConditionalRoute

export interface ElementConditionalRoute {
    type?: 'ElementConditionalRoute'
    routeTo: StepId | 'ConditionalRoute.EndOfSection'
    elementId: ElementId
    condition: RouteCondition
}

export interface UnitDataConditionalRoute {
    type: 'UnitDataConditionalRoute'
    routeTo: StepId | 'ConditionalRoute.EndOfSection'
    variableName: string
    operator: '<=' | '<' | '==' | '!=' | '>' | '>='
    value: string
}

export function isElementConditionalRoute(route: ConditionalRoute): route is ElementConditionalRoute {
    return 'elementId' in route
}

export function isUnitDataConditionalRoute(route: ConditionalRoute): route is UnitDataConditionalRoute {
    return route.type === 'UnitDataConditionalRoute'
}

export type Element =
    | ConfirmationElement
    | YesNoElement
    | NumberElement
    | RadioElement
    | CheckboxElement
    | ImageSelectElement
    | SectionElement
    | CameraElement
    | CalculationElement
    | TextCaptureElement
    | ImageContentElement
    | TextContentElement
    | DateTimeElement
    | SignatureElement
    | DropdownElement
    | FilesSelectElement
    | UnitSelectElement
    | VideoContentElement
    | LinkElement
    | SubtaskElement
    | LoopElement
    | GuideElement

export type NestedElement =
    | NestedConfirmationElement
    | NestedYesNoElement
    | NestedNumberElement
    | NestedRadioElement
    | NestedCheckboxElement
    | NestedImageSelectElement
    | NestedCameraElement
    | NestedCalculationElement
    | NestedTextCaptureElement
    | NestedImageContentElement
    | NestedTextContentElement
    | NestedDateTimeElement
    | NestedSignatureElement
    | NestedDropdownElement
    | NestedFilesSelectElement
    | NestedUnitSelectElement
    | NestedVideoContentElement
    | NestedLinkElement
    | NestedSubtaskElement
    | NestedGuideElement

export type ConfirmationElement = NestedConfirmationElement & ShowIfs<true>
export type YesNoElement = NestedYesNoElement & ShowIfs<'yes' | 'no'>
export type NumberElement = NestedNumberElement & ShowIfs<number>
export type RadioElement = NestedRadioElement & ShowIfs<ChoiceId>
export type CheckboxElement = NestedCheckboxElement & ShowIfs<ChoiceId[]>
export type ImageSelectElement = NestedImageSelectElement & ShowIfs<ChoiceId[]>
export type CameraElement = NestedCameraElement
export type CalculationElement = NestedCalculationElement & ShowIfs<number>
export type TextCaptureElement = NestedTextCaptureElement & ShowIfs<string>
export type ImageContentElement = NestedImageContentElement
export type TextContentElement = NestedTextContentElement
export type SignatureElement = NestedSignatureElement
export type SectionElement = Section & Required & { label: string }
export type DateTimeElement = NestedDateTimeElement
export type DropdownElement = NestedDropdownElement & ShowIfs<ChoiceId[]>
export type FilesSelectElement = NestedFilesSelectElement
export type UnitSelectElement = NestedUnitSelectElement
export type VideoContentElement = NestedVideoContentElement
export type LinkElement = NestedLinkElement
export type SubtaskElement = NestedSubtaskElement
export enum ContentLoopOrderBy {
    unitName = 'unitName',
    attribute = 'attribute',
}

export type ContentLoopElementOrder =
    | {
          orderBy?: ContentLoopOrderBy.unitName
      }
    | {
          orderBy: ContentLoopOrderBy.attribute
          orderByAttribute: string
      }

export type LoopElement = _Element<'loop'> & UnitsToLoop & { steps: Step[] } & ContentLoopElementOrder
export type GuideElement = NestedGuideElement

export type NestedConfirmationElement = _Element<'confirmation'> & Required
export type NestedYesNoElement = _Element<'yes-no'> & Required & YesNoOptions
export type NestedNumberElement = _Element<'number'> & Required & Range & UnitOfMeasurement
export type NestedRadioElement = _Element<'radio'> & Required & Choices & ChoiceRange
export type NestedCheckboxElement = _Element<'checkbox'> & Required & Choices & ChoiceRange
export type NestedImageSelectElement = _Element<'image-select'> & Required & ImageChoices & ChoiceRange & ImageSize
export type NestedCameraElement = _Element<'photo'> & Required & CameraRange & CameraOptions
export type NestedCalculationElement = _Element<'calculation'> & Formula
export type NestedTextCaptureElement = _Element<'text-capture'> & Required & TextRange & MultipleLines
export type NestedImageContentElement = _Element<'image-content'> & URL & AnnotationFileId
export type NestedTextContentElement = HtmlTextContentElement | RichTextContentElement
export type NestedDateTimeElement = _Element<'datetime'> & Required & DateTime
export type NestedSignatureElement = _Element<'signature'> & Required
export type NestedDropdownElement = _Element<'dropdown'> & Required & DropdownChoices & ChoiceRange
export type NestedFilesSelectElement = _Element<'files-select'> & Required & { uploadLimit?: number } & AllowedFileTypes
export type NestedUnitSelectElement = _Element<'unit-select'> & Required & UnitsToSelect
export type NestedVideoContentElement = _Element<'video-content'> & URL & { fileId: FileId }
export type NestedLinkElement = _Element<'link'> & URL
export type NestedSubtaskElement = _Element<'flow'> &
    Required &
    QualifiedFlowId &
    ReportRange & { subjectUnitInference?: 'report' | 'loop' }

/**
 * In the Portal the user can choose to display the Guide element by using unit info and tags, rather
 * than just choosing which guide by selecting it. They need to choose which unit it relates to and which tag. With
 * the unit and tag info we will dynamically find the guides to display to the user.
 */
type DynamicGuideSettings = {
    // which unit the guides need to relate to
    source: 'reportSubject' | 'loopSubject'
    // the tags which are on the guides
    tagId: number
}

/**
 * We should really either have a guideId OR dynamicGuideSettings but there would be
 * a difficult migration in the DB
 */
type GuideLinkingElement = _Element<'guide'> &
    Required & {
        guideId?: Guide.Id
    } & {
        dynamicGuideSettings?: DynamicGuideSettings
    }

export type NestedGuideElement = GuideLinkingElement

export type ElementType = Element['type']

type _Element<T> = {
    id: ElementId
    type: T
    label: string
}

export type ShowIf<V> = {
    value: V
    elements: NestedElement[]
}

export type ShowIfs<V> = {
    showIfs?: Array<ShowIf<V>>
}

type QualifiedFlowId = { workflow: number; procedure?: number }
type Required = { required?: boolean }
type YesNoOptions = { useIcons?: boolean; iconSize?: YesNoIconSizeType; yesWeight?: number; noWeight?: number }
type Range = { min?: number; max?: number; step?: number }
type UnitOfMeasurement = { unitOfMeasurement?: string }
type Choices = { choices: Choice[] }
type ImageChoices = { choices: ImageChoice[] }
type ImageSize = { size?: ImageSizeType }
type DropdownChoices = { choices: DropdownChoice[] }
type ChoiceRange = { minChoices?: number; maxChoices?: number }
type ReportRange = { minReports?: number; maxReports?: number }
type CameraRange = { minPhotos?: number; maxPhotos?: number }
type Formula = { formula: Expression; decimalPlaces?: number }
type TextRange = { minChars?: number; maxChars?: number }
type MultipleLines = { multipleLines: boolean }
type URL = { url?: string }
type AnnotationFileId = { annotationFileId?: FileId }
type CameraOptions = {
    // Optional to allow backwards compatibility
    allowGallerySelection?: boolean
    kind?: CameraType
}

type HTML = { sanitizedHTML: string }
type RichText = { richText: RichTextParagraph[] }
export type HtmlTextContentElement = _Element<'text-content'> & HTML & DisplayType
export type RichTextContentElement = _Element<'text-content'> & RichText & DisplayType

export function textContentHasHtml(element: Element): element is HtmlTextContentElement {
    return 'sanitizedHTML' in element
}

export function textContentHasRichText(element: Element): element is RichTextContentElement {
    return 'richText' in element
}

export function isTextContentElement(element: NestedElement): element is NestedTextContentElement {
    return element.type === 'text-content'
}

type DateTime = {
    selectType: 'date' | 'time' | 'datetime'
    futureLimitInDays?: number
    pastLimitInDays?: number
}
type DisplayType = { displayType?: Nullable<DisplayTypeOptions> }
export type DisplayTypeOptions = 'info' | 'warning' | 'problem'

export type Choice = {
    id: ChoiceId
    label: string
}

export type CameraType = 'photo' | 'video' | 'any'

export type DropdownChoice = Choice & {
    value?: string
}

export type ImageChoice = Choice & URL & AnnotationFileId

export type ImageSizeType = 'small' | 'medium' | 'large'

export type YesNoIconSizeType = 'small' | 'medium'

export type UnitsToSelect = {
    unitTypes: number[]
    minUnits?: number
    maxUnits?: number
}

export type UnitsToLoop = {
    unitType: number
    unitFilters: UnitFilter[]
}

export type AllowedFileTypes = {
    fileTypes?: FileSelectExtension[]
}

export type DisplayOptions = 'info' | 'warning' | 'problem'
//
// RouteCondition

export type RouteCondition =
    | NotAnsweredRouteCondition
    | YesNoRouteCondition
    | NumberRouteCondition
    | ContainsChoiceRouteCondition
    | EqualsChoicesRouteCondition
    | EqualsChoiceRouteCondition
    | SectionRouteCondition
    | ContainsStringRouteCondition
    | ConfirmedRouteCondition

export interface NotAnsweredRouteCondition {
    type: 'NotAnsweredRouteCondition'
}

export interface ConfirmedRouteCondition {
    type: 'ConfirmedRouteCondition'
}

export interface YesNoRouteCondition {
    type: 'YesNoRouteCondition'
    equalTo: 'yes' | 'no'
}

export interface NumberRouteCondition {
    type: 'NumberRouteCondition'
    operator: '<=' | '<' | '==' | '!=' | '>' | '>='
    expression: Expression
}

export interface ContainsChoiceRouteCondition {
    type: 'ContainsChoiceRouteCondition'
    choiceId: ChoiceId
}

export interface EqualsChoicesRouteCondition {
    type: 'EqualsChoicesRouteCondition'
    equalTo: ChoiceId[]
}

export interface EqualsChoiceRouteCondition {
    type: 'EqualsChoiceRouteCondition'
    equalTo: ChoiceId
}

export interface SectionRouteCondition {
    type: 'SectionRouteCondition'
    completed: boolean
}

export interface ContainsStringRouteCondition {
    type: 'ContainsStringRouteCondition'
    string: string
}

//
// Answer

export type DateComponents = {
    day: number
    month: number
    year: number
}

export type TimeComponents = {
    hour: number
    minute: number
}

export type DateType = 'date' | 'time' | 'datetime'

export type DateTimeAnswerValue =
    | {
          type: 'date'
          timezoneOffsetInMs?: number
          timezone?: string
          date: DateComponents
      }
    | {
          type: 'time'
          timezoneOffsetInMs?: number
          timezone?: string
          time: TimeComponents
      }
    | {
          type: 'datetime'
          timezoneOffsetInMs?: number
          timezone?: string
          date: DateComponents
          time: TimeComponents
      }

export type SelectedUnits = number[] | 'no-units-available' | [null]
export type ReportIds = ReportId[]

export type ExtensibleFile = PhotoFile | VideoFile

// type check for the value of [null] in SelectedUnits answer
export function isArrayWithNull<T>(arr: T | [null]): arr is [null] {
    return Array.isArray(arr) && arr.length === 1 && arr[0] === null
}

// convenience function to always return a number[] from SelectedUnits
export function getSelectedUnitIdsAsNumbers(selectedUnits: SelectedUnits): number[] {
    if (selectedUnits === 'no-units-available' || isArrayWithNull(selectedUnits)) {
        return []
    }

    return selectedUnits as number[]
}

export const getExtensibleFileId = (file: ExtensibleFile) => (file.type === 'VideoFile' ? file.video : file.photo)
export const removeExtensibleFile = (files: ExtensibleFile[], fileId: FileId) =>
    files.filter((f) => getExtensibleFileId(f) !== fileId)

export interface PhotoFile {
    type: 'PhotoFile'
    photo: FileId
    annotation?: FileId
}

export interface VideoFile {
    type: 'VideoFile'
    video: FileId
}

export function isPhotoFile(file: ExtensibleFile): file is PhotoFile {
    return file.type === 'PhotoFile'
}
export function isVideoFile(file: ExtensibleFile): file is VideoFile {
    return file.type === 'VideoFile'
}

export type Answer =
    | ConfirmationAnswer
    | YesNoAnswer
    | NumberAnswer
    | RadioAnswer
    | CheckboxAnswer
    | ImageSelectAnswer
    | PhotoAnswer
    | TextCaptureAnswer
    | SectionAnswer
    | DateTimeAnswer
    | SignatureAnswer
    | DropdownAnswer
    | ExtensibleFilesAnswer
    | FilesSelectAnswer
    | UnitSelectAnswer
    | ReportsAnswer
    | GuideAnswer

export type GuideAnswerValue = WorkflowGuideAnswer | UrlGuideAnswer | FileGuideAnswer | ReportGuideAnswer

interface GuideAnswerBase<T extends Guide.Variant> {
    type: T
    guideId: Guide.Id
    completed: boolean
}

export interface WorkflowGuideAnswer extends GuideAnswerBase<'workflow'> {
    version: number
}

export interface UrlGuideAnswer extends GuideAnswerBase<'url'> {
    version?: number
}

export interface FileGuideAnswer extends GuideAnswerBase<'file'> {
    version?: number
}

export interface ReportGuideAnswer extends GuideAnswerBase<'report'> {
    version?: number
}

export type DatabaseAnswer = Answer & { syncTime: Date; encryptedValue: Nullable<EncryptedText> }
export function databaseAnswerToAnswer(answer: DatabaseAnswer): Answer {
    return omit(omit(answer, 'syncTime'), 'encryptedValue') as Answer
}

export type DatabaseAnswerWithTags = DatabaseAnswer & { tags: string[] }

export type AnswerType = (typeof answerTypes)[number]

export const answerTypes = createStringEnum(
    'confirmation',
    'yes-no',
    'number',
    'radio',
    'checkbox',
    'image-select',
    'photo',
    'text-capture',
    'section',
    'datetime',
    'signature',
    'dropdown',
    'extensible-files',
    'files-select',
    'unit-select',
    'reports',
    'guide'
)

export type AnswerBase = Omit<_Answer<never, never>, 'type' | 'value'>

export type ConfirmationAnswer = _Answer<'confirmation', Nullable<true>>
export type YesNoAnswer = _Answer<'yes-no', Nullable<'yes' | 'no'>>
export type NumberAnswer = _Answer<'number', Nullable<number>>
export type RadioAnswer = _Answer<'radio', Nullable<ChoiceId>>
export type CheckboxAnswer = _Answer<'checkbox', ChoiceId[]>
export type ImageSelectAnswer = _Answer<'image-select', ChoiceId[]>
export type PhotoAnswer = _Answer<'photo', FileName[]>
export type TextCaptureAnswer = _Answer<'text-capture', string>
export type DateTimeAnswer = _Answer<'datetime', DateTimeAnswerValue>
export type SignatureAnswer = _Answer<'signature', Nullable<FileName>>
export type SectionAnswer = _Answer<'section', 'completed'>
export type DropdownAnswer = _Answer<'dropdown', Nullable<ChoiceId[]>>
export type ExtensibleFilesAnswer = _Answer<'extensible-files', ExtensibleFile[]>
export type FilesSelectAnswer = _Answer<'files-select', FileId[]>
export type UnitSelectAnswer = _Answer<'unit-select', SelectedUnits>
export type ReportsAnswer = _Answer<'reports', ReportIds>
// TODO: Remove GuideAnswerValue once all answers are migrated to the new format (Zap-5086)
export type GuideAnswer = _Answer<'guide', GuideAnswerValue | GuideAnswerValue[]>

export type _Answer<T extends AnswerType, V> = {
    type: T
    id: AnswerId
    element: ElementId
    latitude: Nullable<number>
    longitude: Nullable<number>
    report: ReportId
    timestamp: Date
    user: number
    value: V
    oldAnswer?: Nullable<AnswerId>
    automaticallyGenerated?: Nullable<boolean>
    loopPath?: Nullable<LoopPath>
    tags?: Nullable<string[]>
    /**
     * If this is true it means the element that is relates to was created dynamically
     * in code rather than being based on an element the user added in the workflow via Portal. An example
     * of this are the unit select filters which allow the user to select the units they want
     * to loop over at the time they do the task.
     *
     * Most answers will not be dynamic elements so this should be used infrequently
     */
    dynamicElement?: Nullable<boolean>
}

//
// Summary

export type FlowSummary = SectionSummary

export interface SectionSummary {
    sectionId: ElementId
    steps: Array<{ stepId: StepId; loopPath: LoopPath }>
    elements: Array<{ elementId: ElementId; loopPath: LoopPath }>
    answers: Answer[]
    completedDate: Date | null
    completedBy: number | null
    conflicts: Array<[AnswerId, AnswerId]>
    contributors: number[]
    validationFailures: ValidationFailure[]
    calculations: Array<{ elementId: ElementId; loopPath: LoopPath; result: CalculationResult }>
    inProgressState: Array<{ elementId: ElementId; loopPath: LoopPath }>
}

export type SavedFlowSummary = Pick<FlowSummary, 'steps' | 'elements' | 'calculations'> & {
    answers: AnswerId[]
}

export interface ValidationFailure {
    sectionId: ElementId
    elementId: ElementId
    loopPath: LoopPath
    reason: FailureReason
}

export type FailureReason =
    | 'required'
    | 'min'
    | 'max'
    | 'minChoices'
    | 'maxChoices'
    | 'minReports'
    | 'maxReports'
    | 'minPhotos'
    | 'maxPhotos'
    | 'invalidSection'
    | 'minChars'
    | 'maxChars'
    | 'futureLimitInDays'
    | 'pastLimitInDays'
    | 'minUnits'
    | 'maxUnits'
    | 'incompleteSubtask'
    | 'uploadLimit'

export interface CalculationResult {
    value: number
    timestamp: Date
}

export type ValidationError = LegacySanitizedHtmlFound | GuideNotFoundError | InvalidGuideElementFound

interface LegacySanitizedHtmlFound {
    type: 'LegacySanitizedHtmlFound'
    elementId: ElementId
}

interface GuideNotFoundError {
    type: 'GuideNotFound'
    guideId: Guide.Id
}

interface InvalidGuideElementFound {
    type: 'InvalidGuideElementFound'
    guideId: Guide.Id
}

export function isAnswerOfType<T extends AnswerType>(
    type: T,
    answer?: Answer | null
): answer is Extract<Answer, { type: T }> {
    return answer?.type === type
}

/**
 * @deprecated Use isAnswerOfType
 */
export function isReportsAnswer(answer: Answer): answer is ReportsAnswer {
    return isAnswerOfType('reports', answer)
}
